// React
import type { FC } from 'react';

// Mantine
import { Badge, Menu, UnstyledButton, useMantineTheme } from '@mantine/core';

// Constants
import { IconChevronDown } from '@tabler/icons-react';
import { getUploadStatusBadgeProps } from '../uploadStatus';

export interface UploadStatusMenuProps {
  status: string | null;
  statusOptions: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder: string;
}

export const UploadStatusMenu: FC<UploadStatusMenuProps> = ({
  status,
  statusOptions,
  onChange,
  disabled = false,
  placeholder,
}) => {
  const theme = useMantineTheme();
  const currentValue = status ?? null;
  const currentLabel =
    statusOptions.find((option) => option.value === currentValue)?.label ??
    status ??
    placeholder;
  const badgeProps = getUploadStatusBadgeProps(status ?? '', theme);

  if (disabled) {
    return (
      <Badge radius="sm" size="md" {...badgeProps}>
        {currentLabel}
      </Badge>
    );
  }

  return (
    <Menu position="bottom-start" shadow="sm" withinPortal>
      <Menu.Target>
        <UnstyledButton style={{ display: 'inline-flex', cursor: 'pointer' }}>
          <Badge
            radius="sm"
            size="md"
            rightSection={<IconChevronDown size={14} stroke={1.5} />}
            {...badgeProps}
          >
            {currentLabel}
          </Badge>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {statusOptions.map((option) => {
          const optionBadgeProps = getUploadStatusBadgeProps(option.value, theme);
          const isActive = option.value === currentValue;
          return (
            <Menu.Item
              key={option.value}
              onClick={() => {
                if (isActive) return;
                onChange(option.value);
              }}
              disabled={isActive}
            >
              <Badge radius="sm" size="md" {...optionBadgeProps}>
                {option.label}
              </Badge>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
